import { DragEvent, useCallback, useEffect, useRef, useState } from 'react'

interface UseDragAndDropProps {
  onReorder: (fromIndex: number, toIndex: number) => void
  scrollEdgeSize?: number
  scrollSpeed?: number
  dragImageClassName?: string
}

const findScrollableAncestor = (el: HTMLElement | null): HTMLElement | null => {
  let current = el?.parentElement ?? null
  while (current && current !== document.body) {
    const overflowY = window.getComputedStyle(current).overflowY
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll'
    if (isScrollable && current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return null
}

export const useDragAndDrop = ({
  onReorder,
  scrollEdgeSize = 60,
  scrollSpeed = 12,
  dragImageClassName,
}: UseDragAndDropProps) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const scrollContainerRef = useRef<HTMLElement | null>(null)
  const scrollDirectionRef = useRef<'up' | 'down' | null>(null)
  const rafIdRef = useRef<number | null>(null)

  const stopAutoScroll = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
    scrollDirectionRef.current = null
  }, [])

  const startAutoScroll = useCallback(() => {
    if (rafIdRef.current !== null) return
    const tick = () => {
      const container = scrollContainerRef.current
      const direction = scrollDirectionRef.current
      if (!container || !direction) {
        rafIdRef.current = null
        return
      }
      container.scrollTop += direction === 'up' ? -scrollSpeed : scrollSpeed
      rafIdRef.current = requestAnimationFrame(tick)
    }
    rafIdRef.current = requestAnimationFrame(tick)
  }, [scrollSpeed])

  const handleGlobalDragOver = useCallback(
    (event: globalThis.DragEvent) => {
      const container = scrollContainerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      const fromTop = event.clientY - rect.top
      const fromBottom = rect.bottom - event.clientY

      const canScrollUp = container.scrollTop > 0
      const canScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight

      if (fromTop < scrollEdgeSize && fromTop >= 0 && canScrollUp) {
        scrollDirectionRef.current = 'up'
        startAutoScroll()
      } else if (fromBottom < scrollEdgeSize && fromBottom >= 0 && canScrollDown) {
        scrollDirectionRef.current = 'down'
        startAutoScroll()
      } else {
        stopAutoScroll()
      }
    },
    [scrollEdgeSize, startAutoScroll, stopAutoScroll],
  )

  const teardown = useCallback(() => {
    stopAutoScroll()
    document.removeEventListener('dragover', handleGlobalDragOver)
    scrollContainerRef.current = null
  }, [handleGlobalDragOver, stopAutoScroll])

  const reset = useCallback(() => {
    teardown()
    setDraggedIndex(null)
    setDragOverIndex(null)
  }, [teardown])

  useEffect(() => {
    return () => {
      teardown()
    }
  }, [teardown])

  const getItemProps = (index: number) => ({
    'data-drag-item-index': String(index),
    onDragOver: (event: DragEvent<HTMLElement>) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      if (dragOverIndex !== index) setDragOverIndex(index)
    },
    onDragLeave: () => {
      setDragOverIndex((current) => (current === index ? null : current))
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      event.preventDefault()
      if (draggedIndex !== null && draggedIndex !== index) {
        onReorder(draggedIndex, index)
      }
      reset()
    },
  })

  const getHandleProps = (index: number) => ({
    draggable: true,
    onDragStart: (event: DragEvent<HTMLElement>) => {
      event.dataTransfer.effectAllowed = 'move'

      const handle = event.currentTarget as HTMLElement
      const itemEl = handle.closest<HTMLElement>(`[data-drag-item-index="${index}"]`)
      if (itemEl) {
        const rect = itemEl.getBoundingClientRect()

        let dragImageEl: HTMLElement = itemEl

        if (dragImageClassName) {
          const clone = itemEl.cloneNode(true) as HTMLElement
          clone.style.position = 'fixed'
          clone.style.top = '-9999px'
          clone.style.left = '0'
          clone.style.width = `${rect.width}px`
          clone.style.pointerEvents = 'none'
          dragImageClassName
            .split(' ')
            .filter(Boolean)
            .forEach((cls) => clone.classList.add(cls))
          document.body.appendChild(clone)
          dragImageEl = clone
          setTimeout(() => clone.remove(), 0)
        }

        event.dataTransfer.setDragImage(dragImageEl, event.clientX - rect.left, event.clientY - rect.top)
        scrollContainerRef.current = findScrollableAncestor(itemEl)
      }

      document.addEventListener('dragover', handleGlobalDragOver)
      setDraggedIndex(index)
    },
    onDragEnd: reset,
  })

  return {
    getItemProps,
    getHandleProps,
    draggedIndex,
    dragOverIndex,
  }
}
